// Specs (ERP-PLAN.md §5.11): every spec with its status, the parts in active BOMs that still need one,
// and change notices. ?tab=specs|needs|ecn; ?open=<item id> opens a spec in the side pane (<notice id> on the ECN tab).
import { FileText } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { componentsOf, ownerLabel, revisionState } from "../../../../shared/boms.ts";
import type { Item, Spec } from "../../../../shared/db.gen.ts";
import { ITEM_TYPE_LABELS, ITEM_TYPES, typeLabel, typeOf } from "../../../../shared/part-type.ts";
import { inProject, partNeeds } from "../../../../shared/projects.ts"; // Packaging ---
import { type SpecStatus, specStatusLabel } from "../../../../shared/specs.ts";
import { useBomIndex } from "../../lib/boms.ts";
import { ago } from "../../lib/format.ts";
import { can } from "../../lib/mutate.ts";
import { projectsOfItem, useProjectIndex } from "../../lib/projects.ts"; // Manufacturing projects ---
import { setSearch, useSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Select } from "../../ui/controls.tsx";
import type { Column } from "../../ui/DataTable.tsx";
import { ListPage } from "../../ui/ListPage.tsx";
import { ChangeNoticesTab } from "./ChangeNotices.tsx";
import { day, startSpec, statusOf, useSpecIndex } from "./lib.ts";
import { SpecStatusBadge } from "./SpecWorkspace.tsx";

const OPEN_PO = new Set(["sent", "acknowledged", "in_production", "shipped"]);
// A Shopify item's spec is its product's (migration 059): named by the product.
const itemTitle = (i: Item) => (i.source === "shopify" && i.shopify_product_title ? i.shopify_product_title : i.name);

type Tab = "specs" | "needs" | "ecn";

export function ProductionDocsPage() {
  const params = useSearch();
  const tab = (params.get("tab") as Tab | null) ?? "specs";
  const specs = useRows("spec");
  const needs = useNeedsSpec();
  const notices = useRows("change_notice");
  const drafts = notices.filter((n) => n.status === "draft").length;
  const tabs = (
    <div className="flex gap-4 border-b border-line bg-surface px-4 text-[12px]">
      {(
        [
          ["specs", "Specs", specs.length],
          ["needs", "Needs a spec", needs.length],
          ["ecn", "Change notices", drafts ? `${notices.length} · ${drafts} draft` : notices.length],
        ] as const
      ).map(([t, label, n]) => (
        <button
          key={t}
          type="button"
          onClick={() => setSearch({ tab: t === "specs" ? null : t, open: null, project: null, kind: null, status: null })}
          className={cx("-mb-px border-b-2 py-2", tab === t ? "border-accent text-text" : "border-transparent text-muted hover:text-text")}
        >
          {label} <span className="text-faint">{n}</span>
        </button>
      ))}
    </div>
  );
  if (tab === "ecn") return <ChangeNoticesTab tabs={tabs} />;
  if (tab === "needs") return <NeedsSpecTab tabs={tabs} rows={needs} />;
  return <SpecsTab tabs={tabs} />;
}

type SpecRow = { id: string; item: Item; spec: Spec; status: SpecStatus; files: number; releasedAt: string | null; olderOnPos: number };

function SpecsTab({ tabs }: { tabs: ReactNode }) {
  const ix = useSpecIndex();
  const px = useProjectIndex();
  const items = useRows("item");
  const projects = useRows("project");
  const pos = useRows("po");
  const poLines = useRows("po_line");
  const params = useSearch();
  const project = params.get("project") ?? "";
  const kind = params.get("kind") ?? "";
  const status = params.get("status") ?? "";

  const rows = useMemo<SpecRow[]>(() => {
    const itemById = new Map(items.map((i) => [i.id, i]));
    // Open POs sent at a revision that's no longer current ("Rev C released after this PO was sent").
    const open = new Set(pos.filter((p) => OPEN_PO.has(p.status)).map((p) => p.id));
    const older = new Map<string, Set<string>>();
    for (const l of poLines) {
      if (!l.spec_revision_id || !open.has(l.po_id)) continue;
      const spec = ix.specByItem.get(l.item_id);
      if (spec && spec.current_revision_id !== l.spec_revision_id) older.set(l.item_id, (older.get(l.item_id) ?? new Set()).add(l.po_id));
    }
    const out: SpecRow[] = [];
    for (const spec of ix.specs) {
      // A product's spec (migration 059) is listed once, under its first variant, named by the product.
      const item = itemById.get(ix.itemOfSpec.get(spec.id) ?? "");
      if (!item) continue;
      const current = spec.current_revision_id ? ix.revisionById.get(spec.current_revision_id) : undefined;
      out.push({
        id: item.id,
        item,
        spec,
        status: statusOf(ix, spec),
        files: (ix.filesBySpec.get(spec.id) ?? []).filter((f) => f.in_draft).length,
        releasedAt: current?.released_at ?? null,
        olderOnPos: older.get(item.id)?.size ?? 0,
      });
    }
    return out;
  }, [ix, items, pos, poLines]);

  // Manufacturing projects --- a sell item's project, a part's from the BOMs that use it (a shared board under both)
  const shown = rows.filter((r) => (!project || inProject(px, r.item, project)) && (!kind || typeOf(r.item) === kind) && (!status || r.status.kind === status));
  const projectName = new Map(projects.map((p) => [p.id, p.name]));
  const projectsText = (item: Item) => {
    const p = projectsOfItem(px, item);
    return [...p.inUse.map((id) => projectName.get(id) ?? ""), ...p.onDraft.map((id) => `${projectName.get(id) ?? ""} (draft)`)].join(", ");
  };

  const columns: Column<SpecRow>[] = [
    {
      key: "name",
      header: "Item",
      width: "minmax(240px,3fr)",
      value: (r) => itemTitle(r.item),
      cell: (r) => <span className="truncate font-medium">{itemTitle(r.item)}</span>,
    },
    { key: "sku", header: "SKU", width: "150px", value: (r) => r.item.sku, cell: (r) => <span className="truncate font-mono text-[12px]">{r.item.sku}</span> },
    { key: "kind", header: "Type", width: "120px", value: (r) => typeLabel(r.item) },
    {
      key: "project",
      header: "Projects",
      width: "140px",
      value: (r) => projectsText(r.item),
      cell: (r) => <span className="truncate">{projectsText(r.item)}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "190px",
      value: (r) => specStatusLabel(r.status),
      cell: (r) => (
        <span className="flex items-center gap-1">
          <SpecStatusBadge status={r.status} />
          {r.olderOnPos > 0 && (
            <span title={`${r.olderOnPos} open ${r.olderOnPos === 1 ? "PO was" : "POs were"} sent at an earlier revision`}>
              <Badge tone="danger">{r.olderOnPos} PO</Badge>
            </span>
          )}
        </span>
      ),
    },
    { key: "released", header: "Released", width: "120px", value: (r) => r.releasedAt ?? "", cell: (r) => day(r.releasedAt) },
    { key: "files", header: "Files", width: "70px", align: "right" },
    {
      key: "updated",
      header: "Draft saved",
      width: "130px",
      value: (r) => r.spec.updated_at,
      cell: (r) => <span className="text-muted">{ago(r.spec.updated_at)}</span>,
    },
  ];

  return (
    <ListPage
      title="Specs"
      rows={shown}
      columns={columns}
      defaultSort={{ key: "name" }}
      banner={tabs}
      pane="spec"
      toolbar={
        <div className="flex items-center gap-2 [[data-stacked]_&]:flex-wrap">
          <div className="w-44">
            <Select
              value={project}
              onChange={(e) => setSearch({ project: e.target.value })}
              options={[
                { value: "", label: "All projects" },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
                { value: "none", label: "In no project" },
              ]}
            />
          </div>
          <div className="w-40">
            <Select
              value={kind}
              onChange={(e) => setSearch({ kind: e.target.value })}
              options={[
                { value: "", label: "All types" },
                { value: "sell", label: "Sell item" },
                ...ITEM_TYPES.map((t) => ({ value: t, label: ITEM_TYPE_LABELS[t] })),
              ]}
            />
          </div>
          <div className="w-44">
            <Select
              value={status}
              onChange={(e) => setSearch({ status: e.target.value })}
              options={[
                { value: "", label: "Any status" },
                { value: "draft", label: "Draft (never released)" },
                { value: "ahead", label: "Draft ahead of release" },
                { value: "released", label: "Released, up to date" },
              ]}
            />
          </div>
        </div>
      }
      empty={
        <EmptyState icon={<FileText className="size-8" />} title={rows.length ? "No specs match" : "No specs yet"}>
          Start one from an item's Spec section, or from “Needs a spec”.
        </EmptyState>
      }
    />
  );
}

type NeedRow = { id: string; item: Item; boms: string[] };

/** Materials and sub-assemblies in active BOMs that have no spec. */
function useNeedsSpec(): NeedRow[] {
  const bomIx = useBomIndex();
  const ix = useSpecIndex();
  return useMemo(() => {
    const usedIn = new Map<string, Set<string>>();
    for (const bom of bomIx.bomById.values()) {
      const state = revisionState(bomIx, bom);
      if (state !== "active" && state !== "scheduled") continue;
      for (const id of componentsOf(bomIx, bom.id)) usedIn.set(id, (usedIn.get(id) ?? new Set()).add(ownerLabel(bomIx, bom)));
    }
    const out: NeedRow[] = [];
    for (const [id, boms] of usedIn) {
      const item = bomIx.itemById.get(id) as Item | undefined;
      if (!item || item.archived || (item.kind !== "material" && item.kind !== "subassembly") || ix.specByItem.has(id)) continue;
      if (!partNeeds(item).spec) continue; // Packaging --- plain or labelled packaging: nothing to tell a vendor but the part
      out.push({ id, item, boms: [...boms].sort() });
    }
    return out;
  }, [bomIx, ix]);
}

function NeedsSpecTab({ tabs, rows }: { tabs: ReactNode; rows: NeedRow[] }) {
  const columns: Column<NeedRow>[] = [
    {
      key: "name",
      header: "Item",
      width: "minmax(220px,2fr)",
      value: (r) => r.item.name,
      cell: (r) => <span className="truncate font-medium">{r.item.name}</span>,
    },
    { key: "sku", header: "SKU", width: "150px", value: (r) => r.item.sku, cell: (r) => <span className="truncate font-mono text-[12px]">{r.item.sku}</span> },
    { key: "kind", header: "Type", width: "120px", value: (r) => typeLabel(r.item) },
    {
      key: "boms",
      header: "Used in",
      width: "minmax(240px,3fr)",
      value: (r) => r.boms.join(", "),
      cell: (r) => (
        <span className="truncate text-muted" title={r.boms.join("\n")}>
          {r.boms.length > 2 ? `${r.boms.slice(0, 2).join(", ")} +${r.boms.length - 2}` : r.boms.join(", ")}
        </span>
      ),
    },
    {
      key: "start",
      header: "",
      width: "120px",
      value: () => "",
      cell: (r) =>
        can("spec.create") ? (
          <Button
            size="sm"
            variant="ghost"
            icon={<FileText className="size-3.5" />}
            onClick={(e) => {
              e.stopPropagation();
              // The new spec shows at once (optimistic), so open it without waiting for the server.
              startSpec(r.item.id);
              setSearch({ tab: null, open: r.item.id });
            }}
          >
            Start spec
          </Button>
        ) : null,
    },
  ];
  return (
    <ListPage
      title="Specs"
      rows={rows}
      columns={columns}
      defaultSort={{ key: "sku" }}
      banner={tabs}
      pane="spec"
      empty={
        <EmptyState icon={<FileText className="size-8" />} title="Every part in an active BOM has a spec">
          Materials and sub-assemblies used by active BOMs show here until they have one.
        </EmptyState>
      }
    />
  );
}
